import { initializeApp } from "firebase/app";
import { firebaseConfig } from "../configs/firebase.config";
import { getDatabase, ref, update, push, onValue, onDisconnect, remove, serverTimestamp } from "firebase/database";

// Initialize Firebase
export const firebaseApp = initializeApp(firebaseConfig);
const db = getDatabase(firebaseApp);

/**
 * Set user online status under the right structure
 * @param {Object} user
 * @param {string} brandID
 */
export const setUserStatus = async (user, brandID, isOnline = true) => {
  if (!user?.type || !user?.id) {
    return;
  }

  await update(ref(db, `brand-${brandID}/users/${user.type}-${user.id}`), {
	availability: isOnline ? 'active' : 'offline',
	isOnline,
	lastSeen: serverTimestamp()
  });
};

/**
 * Store the explicit chat availability selected by an owner or staff member.
 */
export const setUserAvailability = async (user, brandID, availability) => {
  if (!user?.type || !user?.id || !brandID) return;

  const normalizedAvailability = availability === true || availability === 'available'
    ? 'active'
    : availability === false
      ? 'offline'
      : availability === 'away'
        ? 'offline'
        : ['active', 'offline'].includes(availability)
          ? availability
        : 'offline';

  await update(ref(db, `brand-${brandID}/users/${user.type}-${user.id}`), {
    availability: normalizedAvailability,
    isOnline: normalizedAvailability === 'active',
    lastSeen: serverTimestamp()
  });
};

/**
 * Track each browser tab/connection separately. The user remains online while
 * at least one live connection exists.
 */
export const setupUserPresence = (user, brandID, availability) => {
  if (!user?.type || !user?.id || !brandID) return () => {};

  const connectedRef = ref(db, '.info/connected');
  const connectionsRef = ref(db, `brand-${brandID}/users/${user.type}-${user.id}/connections`);
  let connectionRef = null;
  let disconnectRegistration = null;
  let stopped = false;

  const stopConnectionListener = onValue(connectedRef, async (snapshot) => {
    if (snapshot.val() !== true) return;

    connectionRef = push(connectionsRef);
    disconnectRegistration = onDisconnect(connectionRef);
    await disconnectRegistration.remove();

    if (stopped) {
      await disconnectRegistration.cancel();
      return;
    }

    await update(connectionRef, { connectedAt: serverTimestamp() });
    await setUserAvailability(user, brandID, availability);
  });

  return () => {
    stopped = true;
    stopConnectionListener();

    if (disconnectRegistration) {
      void disconnectRegistration.cancel();
    }
    if (connectionRef) {
      void remove(connectionRef);
    }
  };
};

/**
 * Create or update a chat thread between two users
 */
export const sendMessageToFirebase = async ({
  brandID,
  sender,
  recipient,
  messageText
}) => {
  if (!sender || !recipient || !brandID) {
    return;
  }

  const timestamp = Date.now();

  const senderKey = `${sender.type}-${sender.id}`;
  const recipientKey = `${recipient.type}-${recipient.id}`;

  // ✅ Always generate the same chatId regardless of who is sender/recipient
  const participants = [senderKey, recipientKey].sort(); // sort alphabetically
  const finalChatId = `${participants[0]}-${participants[1]}`;

  const messageId = `msg-${timestamp}`;

  const message = {
    text: messageText,
    sender: `${sender.type}-${sender.id}`,
    timestamp,
    readBy: {
      [`${sender.type}-${sender.id}`]: true,
      [`${recipient.type}-${recipient.id}`]: false
    }
  };

  const updates = {
    [`brand-${brandID}/chats/${finalChatId}/participants/${sender.type}-${sender.id}/read`]: true,
    [`brand-${brandID}/chats/${finalChatId}/participants/${recipient.type}-${recipient.id}/read`]: false,
    [`brand-${brandID}/chats/${finalChatId}/lastMessage`]: {
      text: message.text,
      timestamp: message.timestamp,
      senderId: `${sender.type}-${sender.id}`
    },
    [`brand-${brandID}/chats/${finalChatId}/messages/${messageId}`]: message
  };

  await update(ref(db), updates);
};

export const findChatsForUser = (brandID, userKey, callback) => {
  const chatsRef = ref(getDatabase(), `brand-${brandID}/chats`);
  onValue(chatsRef, (snapshot) => {
    const allChats = snapshot.val() || {};
    const matchingChats = [];

    for (const chatId in allChats) {
      const chat = allChats[chatId];
      const participants = chat?.participants || {};

      // Only push if userKey is actually a participant key
      if (participants.hasOwnProperty(userKey)) {
        matchingChats.push({
          chatId,
          chatData: chat
        });
      }
    }

    callback(matchingChats);
  });
};

/**
 * Get total unread messages count for a user across all chats
 * @param {string} brandID 
 * @param {string} userKey - e.g., "staffs-1", "owner-1", "customers-1"
 * @param {function} callback - Called with the total unread count
 */
export const getUnreadMessagesCount = (brandID, userKey, callback) => {
  const chatsRef = ref(getDatabase(), `brand-${brandID}/chats`);

  onValue(chatsRef, (snapshot) => {
    const allChats = snapshot.val() || {};
    let totalUnreadCount = 0;

    for (const chatId in allChats) {
      const chat = allChats[chatId];
      const participants = chat?.participants || {};
      const messages = chat?.messages || {};

      // Only count if user is a participant
      if (participants.hasOwnProperty(userKey)) {
        // Count unread messages for this user
        for (const messageId in messages) {
          const message = messages[messageId];
          const readBy = message?.readBy || {};

          // If message exists and user hasn't read it
          if (readBy.hasOwnProperty(userKey) && readBy[userKey] === false) {
            totalUnreadCount++;
          }
        }
      }
    }

    callback(totalUnreadCount);
  });
};

/**
 * Mark all messages in a specific chat as read for a user
 * @param {string} brandID 
 * @param {string} chatId 
 * @param {string} userKey 
 */
export const markChatAsRead = async (brandID, chatId, userKey) => {
  const chatRef = ref(getDatabase(), `brand-${brandID}/chats/${chatId}`);

  // Get current chat data
  onValue(chatRef, (snapshot) => {
    const chat = snapshot.val();
    if (!chat) return;

    const messages = chat.messages || {};
    const updates = {};

    // Mark all messages as read for this user
    for (const messageId in messages) {
      updates[`brand-${brandID}/chats/${chatId}/messages/${messageId}/readBy/${userKey}`] = true;
    }

    // Update participant read status
    updates[`brand-${brandID}/chats/${chatId}/participants/${userKey}/read`] = true;

    // Apply updates
    update(ref(db), updates);
  }, { onlyOnce: true });
};
